import { useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ListNode, ListItemNode } from '@lexical/list';
import { $generateHtmlFromNodes } from '@lexical/html';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  PASTE_COMMAND,
  COMMAND_PRIORITY_LOW,
  type EditorState,
  type LexicalEditor
} from 'lexical';
import { imageFilesFrom } from '@/features/attachments/useAttachmentUploads';
import { ImageNode, $createImageNode } from './ImageNode';
import { MentionNode } from './MentionNode';
import { MentionsPlugin, type MentionCandidate } from './MentionsPlugin';
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list';
import { Bold, Italic, List, ListOrdered, Undo2, Redo2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The shape we pass back to consumers on every keystroke. The server expects
 * all three representations:
 *   • bodyJson — Lexical's serialized EditorState (source of truth on the server)
 *   • bodyHtml — rendered HTML, what goes into outbound emails via Graph SendMail
 *   • bodyText — plain-text mirror, what the full-text index sees
 */
export interface RichTextEditorChange {
  bodyJson: string;
  bodyHtml: string;
  bodyText: string;
  isEmpty: boolean;
}

interface Props {
  onChange: (change: RichTextEditorChange) => void;
  placeholder?: string;
  minHeight?: number;
  /** A counter the parent increments after a successful submit to wipe the editor. */
  resetSignal?: number;
  /** Called with pasted image files so the parent can upload them as attachments. */
  onImagePaste?: (files: File[]) => void;
  /**
   * If provided, pasted images upload through this and embed inline (instead of
   * onImagePaste). Resolves to the attachment id + its download URL.
   */
  uploadImage?: (file: File) => Promise<{ id: string; url: string }>;
  /** Called with each inline image's attachment id so the parent can associate it. */
  onInlineImageAdded?: (id: string) => void;
  /**
   * If provided, enables @-mention typeahead. The list is the parent's choice
   * (today: requester + watchers + IT users on a ticket reply). Omitting it
   * disables mentions entirely (e.g. the create-ticket form).
   */
  mentionCandidates?: MentionCandidate[];
  /** Called whenever the set of @-mentioned user ids in the editor changes. */
  onMentionsChanged?: (userIds: string[]) => void;
}

/**
 * Lexical-based rich text editor scoped for the helpdesk. Minimal v1:
 *   bold / italic / lists / undo+redo
 * Image paste, mentions, links land in follow-up slices (paste-screenshot needs
 * the Blob SAS upload flow first).
 */
export function RichTextEditor({
  onChange,
  placeholder = 'Type your reply…',
  minHeight = 120,
  resetSignal,
  onImagePaste,
  uploadImage,
  onInlineImageAdded,
  mentionCandidates,
  onMentionsChanged
}: Props) {
  return (
    <LexicalComposer
      initialConfig={{
        namespace: 'aes-helpdesk-editor',
        nodes: [ListNode, ListItemNode, ImageNode, MentionNode],
        onError: (err) => {
           
          console.error('[lexical]', err);
        },
        theme: {
          paragraph: 'mb-2 last:mb-0',
          list: { ul: 'list-disc pl-6 my-2', ol: 'list-decimal pl-6 my-2' },
          text: { bold: 'font-semibold', italic: 'italic', underline: 'underline' }
        }
      }}
    >
      <div className="rounded-md border bg-background overflow-hidden">
        <Toolbar />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="px-3 py-2.5 outline-none text-sm leading-relaxed"
                style={{ minHeight }}
                aria-label="Editor"
              />
            }
            placeholder={
              <div className="absolute top-2.5 left-3 text-muted-foreground pointer-events-none text-sm select-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <Capture onChange={onChange} />
          <ResetOnSignal signal={resetSignal} />
          <ImagePastePlugin onImagePaste={onImagePaste} uploadImage={uploadImage} onInlineImageAdded={onInlineImageAdded} />
          {mentionCandidates && (
            <MentionsPlugin candidates={mentionCandidates} onMentionsChanged={onMentionsChanged} />
          )}
        </div>
      </div>
    </LexicalComposer>
  );
}

/**
 * Routes pasted image files up to the parent (uploaded as attachments) and stops
 * Lexical from inserting them as text. Non-image pastes fall through unchanged.
 */
function ImagePastePlugin({
  onImagePaste,
  uploadImage,
  onInlineImageAdded
}: {
  onImagePaste?: (files: File[]) => void;
  uploadImage?: (file: File) => Promise<{ id: string; url: string }>;
  onInlineImageAdded?: (id: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!onImagePaste && !uploadImage) return;
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const files = imageFilesFrom(event.clipboardData);
        if (files.length === 0) return false;
        event.preventDefault();

        if (uploadImage) {
          // Upload then insert each image inline. Uploads resolve asynchronously
          // and out of order, so each insertion must be self-contained:
          //   • append the image node at the current caret (or end of doc), then
          //   • drop a caret AFTER it via a trailing space.
          // Without the trailing-space caret, a freshly inserted DecoratorNode is
          // left node-SELECTED — so the NEXT image's insertNodes() would REPLACE
          // it instead of appending, silently dropping the first image (it then
          // lingers as an orphaned upload). See bug: "two images, only one saved".
          for (const file of files) {
            void uploadImage(file).then(({ id, url }) => {
              editor.update(() => {
                const node = $createImageNode(id, url, file.name);
                const sel = $getSelection();
                if ($isRangeSelection(sel)) {
                  sel.insertNodes([node]);
                } else {
                  $getRoot().append(node);
                }
                const caret = $createTextNode(' ');
                node.insertAfter(caret);
                caret.selectEnd();
              });
              onInlineImageAdded?.(id);
            });
          }
        } else {
          onImagePaste?.(files);
        }
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onImagePaste, uploadImage, onInlineImageAdded]);
  return null;
}

function Toolbar() {
  const [editor] = useLexicalComposerContext();
  return (
    <div className="flex items-center gap-0.5 px-1.5 py-1 border-b bg-muted/30">
      <Btn title="Bold (Ctrl+B)" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}><Bold className="w-3.5 h-3.5" /></Btn>
      <Btn title="Italic (Ctrl+I)" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}><Italic className="w-3.5 h-3.5" /></Btn>
      <Sep />
      <Btn title="Bulleted list" onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}><List className="w-3.5 h-3.5" /></Btn>
      <Btn title="Numbered list" onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}><ListOrdered className="w-3.5 h-3.5" /></Btn>
      <Sep />
      <Btn title="Undo (Ctrl+Z)" onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}><Undo2 className="w-3.5 h-3.5" /></Btn>
      <Btn title="Redo (Ctrl+Y)" onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}><Redo2 className="w-3.5 h-3.5" /></Btn>
    </div>
  );
}

function Btn({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      aria-label={title}
      className={cn(
        'inline-flex items-center justify-center w-7 h-7 rounded text-muted-foreground',
        'hover:bg-muted hover:text-foreground transition-colors'
      )}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="w-px h-4 bg-border mx-1" />;
}

/**
 * Bridges Lexical's editor state out to the parent. Runs inside an editor.read()
 * so $generateHtmlFromNodes works correctly. isEmpty short-circuits trivial
 * "only whitespace" submissions before we waste a network round-trip.
 */
function Capture({ onChange }: { onChange: (c: RichTextEditorChange) => void }) {
  // The editor instance comes through OnChangePlugin's second arg; no need to
  // pull it from context separately.
  return (
    <OnChangePlugin
      onChange={(state: EditorState, lexicalEditor: LexicalEditor) => {
        state.read(() => {
          const text = $getRoot().getTextContent();
          const html = $generateHtmlFromNodes(lexicalEditor);
          const json = JSON.stringify(state.toJSON());
          onChange({
            bodyJson: json,
            bodyHtml: html,
            bodyText: text,
            isEmpty: text.trim().length === 0
          });
        });
      }}
    />
  );
}

/**
 * Resets the editor's state to empty whenever the `signal` prop changes.
 * Used after a successful submit so the reply box clears without forcing
 * a full component remount (which would lose the LexicalComposer instance).
 */
function ResetOnSignal({ signal }: { signal: number | undefined }) {
  const [lexicalEditor] = useLexicalComposerContext();
  useEffect(() => {
    if (signal === undefined || signal === 0) return;
    lexicalEditor.update(() => {
      const root = $getRoot();
      root.clear();
    });
  }, [lexicalEditor, signal]);
  return null;
}
